// DetectorSort.tsx
import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ais-ui/ui-core';
import {
  PiCalendarBlank,
  PiTextAa,
  PiTag,
  PiSortAscending,
  PiSortDescending,
} from 'react-icons/pi';
import { DetectorSortConfig, DetectorSortType } from './types';
import { SortButton } from '../../../components/SortButton';

interface DetectorSortProps {
  sortConfig: DetectorSortConfig;
  setSortConfig: React.Dispatch<React.SetStateAction<DetectorSortConfig>>;
}

const getSortIcon = (type: DetectorSortType) => {
  switch (type) {
    case 'name':
      return PiTextAa;
    case 'type':
      return PiTag;
    case 'date':
      return PiCalendarBlank;
    default:
      return PiSortAscending;
  }
};

const getSortLabel = (type: DetectorSortType) => {
  switch (type) {
    case 'name':
      return 'Name';
    case 'type':
      return 'Type';
    case 'date':
      return 'Date';
    default:
      return 'Sort';
  }
};

export const DetectorSort = ({
  sortConfig,
  setSortConfig,
}: DetectorSortProps) => {
  const handleSortChange = (
    type: DetectorSortType,
    direction?: 'asc' | 'desc'
  ) => {
    setSortConfig({
      type,
      direction:
        direction ||
        (type === sortConfig.type && sortConfig.direction === 'asc'
          ? 'desc'
          : 'asc'),
    });
  };

  const toggleDirection = () => {
    setSortConfig((prev) => ({
      ...prev,
      direction: prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SortButton
          direction={sortConfig.direction}
          label={getSortLabel(sortConfig.type)}
          hideLabel={true}
          asChild={true}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
          Sort by
        </div>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => handleSortChange('name')}
          className={sortConfig.type === 'name' ? 'bg-accent' : ''}
        >
          <PiTextAa className="mr-2 h-4 w-4" />
          Name
          {sortConfig.type === 'name' && (
            <div className="ml-auto">
              {sortConfig.direction === 'asc' ? (
                <PiSortAscending className="h-4 w-4" />
              ) : (
                <PiSortDescending className="h-4 w-4" />
              )}
            </div>
          )}
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => handleSortChange('type')}
          className={sortConfig.type === 'type' ? 'bg-accent' : ''}
        >
          <PiTag className="mr-2 h-4 w-4" />
          Type
          {sortConfig.type === 'type' && (
            <div className="ml-auto">
              {sortConfig.direction === 'asc' ? (
                <PiSortAscending className="h-4 w-4" />
              ) : (
                <PiSortDescending className="h-4 w-4" />
              )}
            </div>
          )}
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => handleSortChange('date')}
          className={sortConfig.type === 'date' ? 'bg-accent' : ''}
        >
          <PiCalendarBlank className="mr-2 h-4 w-4" />
          Date Created
          {sortConfig.type === 'date' && (
            <div className="ml-auto">
              {sortConfig.direction === 'asc' ? (
                <PiSortAscending className="h-4 w-4" />
              ) : (
                <PiSortDescending className="h-4 w-4" />
              )}
            </div>
          )}
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={toggleDirection}>
          <div className="flex items-center">
            {sortConfig.direction === 'asc' ? (
              <>
                <PiSortDescending className="mr-2 h-4 w-4" />
                Switch to descending
              </>
            ) : (
              <>
                <PiSortAscending className="mr-2 h-4 w-4" />
                Switch to ascending
              </>
            )}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
